// src/pages/enterprise-ai/TranscriptsPage.js
//
// The call-transcript library (Chunk T). Reuses the EXACT Genesis upload component
// (TranscriptPicker + ingestTranscript, imported from GenesisPage, not forked),
// then lists transcripts from GET /api/admin/genesis/transcripts with their digest
// state. If that GET is not live yet, the upload zone still works and the list shows
// a clean 'awaiting first sync' state that retries on window focus (never blocks).
//
// Each row supports inline rename (click-to-edit sourceLabel, Enter/blur saves via
// PATCH .../:id, Escape cancels) and delete (ConfirmModal -> DELETE .../:id). Both
// refetch the list on success.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Link,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import axios from 'axios';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { THEME } from './genesisData';
import { TranscriptPicker, ingestTranscript } from './GenesisPage';
import { authHeader, T6_BASE } from '../../hooks/useGtm';

const MOBILE_MQ = '@media (max-width:768px)';

// Modals become full-height sheets under 768px (mirrors the GTM sheet treatment).
const sheetPaperSx = {
  [MOBILE_MQ]: { m: 0, width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100%', borderRadius: 0 },
};

const TONES = {
  amber: { color: THEME.amber, bg: THEME.amberDim },
  mint: { color: THEME.mint, bg: THEME.mintDim },
  red: { color: THEME.pink, bg: 'rgba(198,61,87,0.15)' },
  gray: { color: THEME.textSecondary, bg: 'rgba(255,255,255,0.06)' },
};
const Badge = ({ tone = 'gray', children }) => {
  const t = TONES[tone] || TONES.gray;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px', borderRadius: '4px', background: t.bg, color: t.color, fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1, whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
};

const DIGEST_META = {
  pending: { label: 'Pending', tone: 'amber' },
  digested: { label: 'Digested', tone: 'mint' },
  failed: { label: 'Failed', tone: 'red' },
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(d);
  }
};

// A label:value line (stacks naturally; used for the responsive card meta).
const Meta = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 0.75, fontSize: '12px', flexWrap: 'wrap' }}>
    <Typography component="span" sx={{ fontSize: '12px', color: THEME.textMuted }}>{label}:</Typography>
    <Typography component="span" sx={{ fontSize: '12px', color: THEME.textSecondary }}>{value}</Typography>
  </Box>
);

// Danger-styled confirm dialog (delete). Self-contained; full-height sheet on mobile.
const ConfirmModal = ({ open, title, body, confirmLabel, onConfirm, onCancel, busy, error }) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onCancel}
    PaperProps={{ sx: { background: THEME.card, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}`, borderRadius: '12px', maxWidth: 440, fontFamily: '"IBM Plex Sans", -apple-system, sans-serif', ...sheetPaperSx } }}
  >
    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, pb: 1 }}>{title}</DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6 }}>{body}</Typography>
      {error && <Typography sx={{ fontSize: '12px', color: THEME.pink, mt: 1 }}>{error}</Typography>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', color: THEME.textSecondary, [MOBILE_MQ]: { minHeight: 44 } }}>Cancel</Button>
      <Button
        onClick={onConfirm}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
        sx={{ textTransform: 'none', fontWeight: 700, background: THEME.pink, color: '#fff', boxShadow: 'none', '&:hover': { background: '#b23350', boxShadow: 'none' }, [MOBILE_MQ]: { minHeight: 44 } }}
      >
        {confirmLabel || 'Confirm'}
      </Button>
    </DialogActions>
  </Dialog>
);

const TranscriptRow = ({ t, onRerun, rerunning, onRename, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(t.sourceLabel || '');
  const [savingRename, setSavingRename] = useState(false);
  const [renameError, setRenameError] = useState('');
  // Escape (and the trailing blur that fires when the field unmounts, and the
  // blur after a successful Enter-save) must NOT trigger a save. This ref
  // no-ops the next commit() when a cancel/success already handled the edit.
  const skipCommitRef = useRef(false);

  const meta = t.metadata || {};
  const digest = DIGEST_META[t.digestStatus] || { label: t.digestStatus || 'unknown', tone: 'gray' };
  const companies = Array.isArray(meta.companiesMentioned) ? meta.companiesMentioned : [];
  const hasProposals = Array.isArray(t.linkedProposals) && t.linkedProposals.length > 0;

  // "Extract proofs" — mine "things we do for clients" from THIS transcript into the GTM proofs
  // inventory, alongside Analyze/Re-run digest. POST /api/gtm/proofs/extract is an assumed scoped
  // route (mine-now is fleet-wide); a failure surfaces verbatim, never a fake success.
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState('');
  const extractProofs = async () => {
    setExtracting(true);
    setExtractNote('');
    try {
      const headers = await authHeader();
      const res = await axios.post(`${T6_BASE}/api/gtm/proofs/extract`, { transcriptId: t.transcriptId }, { headers });
      const d = res.data || {};
      const n = d.proofsUpserted != null ? d.proofsUpserted : (d.proposals != null ? d.proposals : null);
      setExtractNote(n != null ? `Extracted ${n} proof${Number(n) === 1 ? '' : 's'}.` : 'Extraction complete.');
    } catch (err) {
      const data = err && err.response && err.response.data;
      setExtractNote((data && (data.detail || data.message || data.error)) || (err && err.message) || 'Extraction failed.');
    } finally {
      setExtracting(false);
    }
  };

  const startEdit = () => {
    skipCommitRef.current = false;
    setRenameError('');
    setDraft(t.sourceLabel || '');
    setEditing(true);
  };
  const cancel = () => {
    skipCommitRef.current = true;
    setDraft(t.sourceLabel || '');
    setEditing(false);
  };
  const commit = async () => {
    if (skipCommitRef.current) { skipCommitRef.current = false; return; }
    const next = draft.trim();
    if (!next || next === (t.sourceLabel || '')) {
      skipCommitRef.current = true;
      setEditing(false);
      return;
    }
    setSavingRename(true);
    try {
      await onRename(t, next);
      skipCommitRef.current = true; // suppress the unmount blur that follows
      setEditing(false);
    } catch (err) {
      setRenameError(err.response?.data?.detail || err.message || 'Rename failed.');
      // stay in edit mode so the value is not lost
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '10px', background: THEME.card, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, [MOBILE_MQ]: { flexDirection: 'column' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {editing ? (
              <TextField
                autoFocus
                value={draft}
                disabled={savingRename}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commit(); }
                  else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                }}
                size="small"
                variant="standard"
                sx={{ minWidth: 200, flex: 1 }}
                InputProps={{ sx: { fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, '&:before': { borderColor: THEME.borderStrong }, '&:after': { borderColor: THEME.mint } } }}
              />
            ) : (
              <Box
                onClick={startEdit}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', minWidth: 0, '&:hover .rename-pencil': { color: THEME.mint } }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, wordBreak: 'break-word' }}>{t.sourceLabel || 'Transcript'}</Typography>
                <EditOutlinedIcon className="rename-pencil" sx={{ fontSize: 14, color: THEME.textMuted, flexShrink: 0 }} />
              </Box>
            )}
            {t.rawFormat && <Badge tone="gray">{t.rawFormat}</Badge>}
            <Badge tone={digest.tone}>{digest.label}</Badge>
          </Box>
          {renameError && <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 0.5 }}>{renameError}</Typography>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
            <Meta label="Call date" value={meta.callDate || 'not stated'} />
            <Meta label="Uploaded" value={`${fmtDate(t.uploadedAt)}${t.uploadedBy ? ` by ${t.uploadedBy}` : ''}`} />
          </Box>
          {companies.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {companies.map((c, i) => <Badge key={i} tone="gray">{c}</Badge>)}
            </Box>
          )}
          {t.digestStatus === 'failed' && t.digestError && (
            <Typography sx={{ fontSize: '11px', color: THEME.pink, mt: 0.75 }}>{t.digestError}</Typography>
          )}
          {extractNote && <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 0.75 }}>{extractNote}</Typography>}
        </Box>

        {/* action row — sits at the card foot on mobile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, [MOBILE_MQ]: { width: '100%', mt: 0.5 } }}>
          {hasProposals && (
            <Link component={RouterLink} to="/genesis" sx={{ fontSize: '12px', color: THEME.mint, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, [MOBILE_MQ]: { minHeight: 44, display: 'inline-flex', alignItems: 'center' } }}>
              View in Genesis
            </Link>
          )}
          {t.digestStatus === 'failed' && (
            <Button size="small" variant="outlined" disabled={rerunning} onClick={() => onRerun(t)}
              sx={{ textTransform: 'none', fontSize: '12px', color: THEME.mint, borderColor: THEME.borderStrong, [MOBILE_MQ]: { minHeight: 44 } }}>
              {rerunning ? 'Queuing…' : 'Re-run digest'}
            </Button>
          )}
          <Button size="small" variant="outlined" disabled={extracting} onClick={extractProofs}
            sx={{ textTransform: 'none', fontSize: '12px', color: THEME.mint, borderColor: THEME.borderStrong, [MOBILE_MQ]: { minHeight: 44 } }}>
            {extracting ? 'Extracting…' : 'Extract proofs'}
          </Button>
          {meta.summary && (
            <Button size="small" onClick={() => setOpen((o) => !o)} endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
              sx={{ textTransform: 'none', fontSize: '12px', color: THEME.textSecondary, [MOBILE_MQ]: { minHeight: 44 } }}>
              {open ? 'Hide' : 'Summary'}
            </Button>
          )}
          <IconButton size="small" aria-label="Delete transcript" onClick={() => onDelete(t)}
            sx={{ color: THEME.textMuted, '&:hover': { color: THEME.pink }, [MOBILE_MQ]: { minHeight: 44, minWidth: 44 } }}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      {open && meta.summary && (
        <Box sx={{ px: 2, pb: 2, pt: 0, borderTop: `1px solid ${THEME.border}` }}>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', mt: 1.5 }}>{meta.summary}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default function TranscriptsPage() {
  const [staged, setStaged] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle | uploading | uploaded | error
  const [uploadError, setUploadError] = useState('');

  const [transcripts, setTranscripts] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [rerunId, setRerunId] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // the transcript pending delete
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const headers = await authHeader();
      const res = await axios.get(`${T6_BASE}/api/admin/genesis/transcripts`, { headers });
      setTranscripts(Array.isArray(res.data && res.data.transcripts) ? res.data.transcripts : []);
    } catch (err) {
      // The list may not be live yet; keep the page usable and show an awaiting state.
      setListError(err.response?.data?.detail || err.message || 'Could not load transcripts.');
      setTranscripts([]);
    } finally {
      setListLoading(false);
      setLoadedOnce(true);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Retry on window focus so the 'awaiting first sync' state self-heals.
  useEffect(() => {
    const onFocus = () => fetchList();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchList]);

  const upload = async () => {
    if (!staged) return;
    setUploadStatus('uploading');
    setUploadError('');
    try {
      await ingestTranscript({ file: staged.file, text: staged.text });
      setUploadStatus('uploaded');
      setStaged(null);
      fetchList();
      setTimeout(() => setUploadStatus('idle'), 2000);
    } catch (err) {
      setUploadStatus('error');
      setUploadError(err.response?.data?.detail || err.message || 'Upload failed.');
    }
  };

  const rerun = async (t) => {
    setRerunId(t.transcriptId);
    try {
      const headers = await authHeader();
      await axios.post(`${T6_BASE}/api/admin/genesis/transcripts/${t.transcriptId}/digest`, {}, { headers });
      fetchList();
    } catch (err) {
      /* leave the row; the list re-fetch shows current state */
    } finally {
      setRerunId(null);
    }
  };

  // Inline rename: PATCH the label, then refetch. Throws propagate to the row so
  // it can keep the field open and surface the error.
  const rename = async (t, sourceLabel) => {
    const headers = await authHeader();
    await axios.patch(`${T6_BASE}/api/admin/genesis/transcripts/${t.transcriptId}`, { sourceLabel }, { headers });
    await fetchList();
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const headers = await authHeader();
      await axios.delete(`${T6_BASE}/api/admin/genesis/transcripts/${confirmDelete.transcriptId}`, { headers });
      setConfirmDelete(null);
      fetchList();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || err.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ color: THEME.textPrimary, fontFamily: '"IBM Plex Sans", -apple-system, sans-serif' }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <DescriptionOutlinedIcon sx={{ color: THEME.mint }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Call transcripts</Typography>
        </Box>
        <Typography sx={{ fontSize: '13px', color: THEME.textMuted, mb: 2.5 }}>
          Upload discovery-call transcripts. Each is digested for its metadata and made available to Genesis.
        </Typography>

        {/* upload zone (reused Genesis picker) */}
        <Box sx={{ border: `1px solid ${THEME.border}`, borderRadius: '12px', background: THEME.card, p: 2, mb: 3 }}>
          <TranscriptPicker onChange={setStaged} status={uploadStatus} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" disabled={!staged || uploadStatus === 'uploading'} onClick={upload}
              startIcon={uploadStatus === 'uploading' ? <CircularProgress size={14} sx={{ color: '#06281f' }} /> : null}
              sx={{ background: THEME.mint, color: '#06281f', textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { background: '#0fa884', boxShadow: 'none' }, [MOBILE_MQ]: { minHeight: 44 } }}>
              {uploadStatus === 'uploading' ? 'Uploading…' : 'Upload transcript'}
            </Button>
            {uploadStatus === 'uploaded' && <Typography sx={{ fontSize: '12px', color: THEME.mint }}>Uploaded. Digesting…</Typography>}
            {uploadStatus === 'error' && <Typography sx={{ fontSize: '12px', color: THEME.pink }}>{uploadError}</Typography>}
          </Box>
        </Box>

        {/* list */}
        {listLoading && !loadedOnce ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: THEME.textMuted }}>
            <CircularProgress size={16} sx={{ color: THEME.mint }} />
            <Typography sx={{ fontSize: '13px' }}>Loading transcripts…</Typography>
          </Box>
        ) : transcripts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textSecondary }}>Awaiting first sync</Typography>
            <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 0.5 }}>
              {listError ? 'The transcript list is not reachable yet. Upload one above, or retry.' : 'No transcripts yet. Upload one above to begin.'}
            </Typography>
            <Button size="small" variant="outlined" onClick={fetchList} sx={{ mt: 1.5, textTransform: 'none', color: THEME.mint, borderColor: THEME.borderStrong, [MOBILE_MQ]: { minHeight: 44 } }}>Retry</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {transcripts.map((t) => (
              <TranscriptRow key={t.transcriptId} t={t} onRerun={rerun} rerunning={rerunId === t.transcriptId} onRename={rename} onDelete={setConfirmDelete} />
            ))}
          </Box>
        )}
      </Box>

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete transcript?"
        body="Deletes the transcript file and its metadata. This cannot be undone."
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onConfirm={doDelete}
        onCancel={() => { if (!deleting) { setConfirmDelete(null); setDeleteError(''); } }}
        busy={deleting}
        error={deleteError}
      />
    </Box>
  );
}
